// src/components/shared/PageInfoStrip.js
// Composição de StatCard(s) + InfoChip(s) (flat ou categorizado) renderizada
// entre TitleBar e DataToolbar (2026-05-11, Fase 10 + Fase 15.2).
//
// API:
//   stats: [{ label, value, total?, sub?, tone?, icon? }]
//   chips: [{ icon?, label?, value }] — flat (homogêneos, ex: DN dynamic)
//   chipGroups: [{ category, chips: [...] }] — categorizado por semântica
//                (Datas / Produtos / Resultados / Manejos)
//
// chips e chipGroups podem coexistir — chips flat renderizam ABAIXO dos groups.

import React from 'react'
import { Box, Typography } from '@mui/material'
import StatCard from './StatCard'
import InfoChip from './InfoChip'

function ChipRow({ items }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items.map((chip, i) => <InfoChip key={i} {...chip} />)}
    </Box>
  )
}

function ChipGroup({ category, chips }) {
  if (!chips || chips.length === 0) return null
  return (
    <Box sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          color: 'text.secondary',
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.05em',
          lineHeight: 1,
          mb: 0.75,
        }}
      >
        {category}
      </Typography>
      <ChipRow items={chips} />
    </Box>
  )
}

export default function PageInfoStrip({ stats = [], chips = [], chipGroups = [] }) {
  const hasStats = stats.length > 0
  const hasChips = chips.length > 0
  const hasGroups = chipGroups.length > 0

  if (!hasStats && !hasChips && !hasGroups) return null

  const hasBelowStats = hasChips || hasGroups

  return (
    <Box component="section" aria-label="Resumo da página" sx={{ mb: 2 }}>
      {hasStats && (
        // Box explícito com display:flex + gap em sx — Stack do MUI usa margins
        // por default (useFlexGap=false) e a prop `gap` não é canônica (só
        // `spacing` é). Padrão usado também em PageActions/ChipRow.
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: hasBelowStats ? 2 : 0,
          }}
        >
          {stats.map((stat, i) => <StatCard key={i} {...stat} />)}
        </Box>
      )}

      {hasGroups && (
        <Box>
          {chipGroups.map((group, i) => (
            <ChipGroup key={i} category={group.category} chips={group.chips} />
          ))}
        </Box>
      )}

      {hasChips && (
        <Box sx={{ mt: hasGroups ? 1.5 : 0 }}>
          <ChipRow items={chips} />
        </Box>
      )}
    </Box>
  )
}
